<template>
	<div
		class="d-flex flex-column amelipro-autocomplete"
		:class="horizontal ? 'flex-md-row' : undefined"
		:style="globalFieldStyles"
	>
		<div
			class="d-inline-flex align-start"
			:class="horizontal ? 'mt-md-2 mr-md-2' : undefined"
		>
			<label
				class="text-body-1 amelipro-autocomplete__label"
				:class="horizontal ? 'mr-md-2' : undefined"
				:for="id"
				:style="labelStyles"
			>
				{{ label }}

				<span v-if="ariaRequired">
					<span aria-hidden="true">
						&nbsp;*
					</span>

					<span class="d-sr-only">
						&nbsp;Champ obligatoire
					</span>
				</span>
			</label>

			<slot name="labelInfo" />
		</div>

		<VAutocomplete
			:id="id"
			ref="inputAutoCompleteField"
			v-model="inputValue"
			:aria-controls="resultListId"
			:aria-describedby="valid ? undefined : `${id}-error`"
			:aria-expanded="isMenuActive ? 'true' : 'false'"
			:aria-invalid="!valid"
			:aria-required="ariaRequired"
			autocomplete="list"
			:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
			class="pt-0 amelipro-text-field"
			color="ap-blue darken-1"
			dense
			:disabled="disabled"
			eager
			:hide-details="hideErrorMessage"
			:items="items"
			outlined
			:placeholder="placeholder"
			:readonly="readonly"
			role="combobox"
			:rules="inputRules"
			:style="inputStyles"
			v-bind="$attrs"
			v-on="$listeners"
			@blur="emitBlurEvent"
			@change="emitChangeEvent"
			@focus="emitFocusEvent"
			@keydown.esc="emitEscEvent"
		>
			<template #append>
				<AmeliproIconBtn
					:id="`${id}-btn`"
					:aria-controls="resultListId"
					:aria-expanded="isMenuActive ? 'true' : 'false'"
					:btn-label="`${label} ouvrir la liste`"
					icon-color="ap-blue darken-1"
					icon-hover-color="ap-blue darken-2"
					tabindex="-1"
				>
					<template #icon>
						{{ isMenuActive ? iconUp : iconDown }}
					</template>
				</AmeliproIconBtn>
			</template>

			<template
				v-if="!hideErrorMessage"
				#message="{ message }"
			>
				<AmeliproMessage
					:id="`${id}-error`"
					no-icon
					text
					type="error"
				>
					<p class="mb-0">
						{{ message }}
					</p>
				</AmeliproMessage>
			</template>
		</VAutocomplete>
	</div>
</template>

<script setup lang="ts">
	import { AutoCompleteItem, InputAutoCompleteField } from './types';
	import { PropType, computed, onMounted, ref } from 'vue';
	import { mdiMenuDown, mdiMenuUp } from '@mdi/js';
	import AmeliproIconBtn from '../AmeliproIconBtn/AmeliproIconBtn.vue';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { IndexedObject } from '../../types';
	import { VAutocomplete } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { convertToHex } from '../../functions/convertToHex';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		globalMaxWidth: {
			type: String,
			default: undefined,
		},
		globalMinWidth: {
			type: String,
			default: undefined,
		},
		globalWidth: {
			type: String,
			default: undefined,
		},
		hideErrorMessage: {
			type: [String, Boolean] as PropType<string | boolean>,
			default: false,
		},
		horizontal: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		inputMaxWidth: {
			type: String,
			default: undefined,
		},
		inputMinWidth: {
			type: String,
			default: undefined,
		},
		items: {
			type: Array as PropType<AutoCompleteItem[]>,
			default: () => [],
		},
		label: {
			type: String,
			required: true,
		},
		labelMaxWidth: {
			type: String,
			default: undefined,
		},
		labelMinWidth: {
			type: String,
			default: undefined,
		},
		placeholder: {
			type: String,
			default: undefined,
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		rules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		value: {
			type: [String, Object] as PropType<string | AutoCompleteItem>,
			default: undefined,
		},
	});

	const emit = defineEmits(['change', 'click', 'esc', 'focus']);

	const inputAutoCompleteField = ref<InputAutoCompleteField>({
		hasMessages: false,
		isMenuActive: false,
		messagesToDisplay: [],
		valid: true,
	});

	// v-model
	const inputValue = computed({
		get: (): string | object | null => props.value,
		set: (newValue: string | object | null) => {
			emit('change', newValue);
		},
	});

	const iconUp = ref(mdiMenuUp);
	const iconDown = ref(mdiMenuDown);
	const isMenuActive = ref(false);
	const valid = ref(true);
	const resultListId = ref<string | null | undefined>(null);
	const config = { attributes: true, childList: false };
	const focused = ref(false);
	const displayError = computed(() => !inputAutoCompleteField.value?.valid && inputAutoCompleteField.value?.hasMessages);

	const setAriaExpanded = (): void => {
		const textFieldWrapper = document.getElementById(`${props.id}-wrapper`);
		isMenuActive.value = inputAutoCompleteField.value.isMenuActive;
		textFieldWrapper?.removeAttribute('aria-expanded');
	};

	const removeIconBtnAttrs = (): void => {
		const btnIcon = document.getElementById(`${props.id}-btn`)?.querySelector('.v-icon');
		btnIcon?.removeAttribute('tabindex');
		btnIcon?.removeAttribute('aria-controls');
		btnIcon?.removeAttribute('aria-expanded');
	};

	const observer = new MutationObserver(setAriaExpanded);
	const btnObserver = new MutationObserver(removeIconBtnAttrs);

	onMounted(() => {
		setAttributes();
	});

	const inputRules = computed<ValidationRule[]>(() => {
		//const { rules } = props; => provoque des mutations indésirables de props.rules
		const rules = [
			...props.rules,
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		return rules;
	});

	const globalFieldStyles = computed<IndexedObject>(() => ({
		maxWidth: props.globalMaxWidth,
		minWidth: props.globalMinWidth,
		width: props.globalWidth,
	}));

	const inputStyles = computed<IndexedObject>(() => ({
		maxWidth: props.inputMaxWidth,
		minWidth: props.inputMinWidth,
	}));

	const labelStyles = computed<IndexedObject>(() => ({
		maxWidth: props.labelMaxWidth,
		minWidth: props.labelMinWidth,
	}));

	// Requis par AmeliproPostalAddressCityRow, pour mimer VTextField :
	// - hasMessages
	// - messagesToDisplay
	// - valid
	//
	// const displayError = computed(() => !inputAutoCompleteField.value?.valid && inputAutoCompleteField.value?.hasMessages);
	const hasMessages = computed<boolean>(() => inputAutoCompleteField.value?.hasMessages);
	const messagesToDisplay = computed<string[]>(() => inputAutoCompleteField.value.messagesToDisplay);
	defineExpose({ hasMessages, messagesToDisplay, valid });

	const setAttributes = (): void => {
		// Champ texte à l'intérieur du composant VAutocomplete
		const textField = document.getElementById(props.id);
		// Balise autour du champ texte
		const textFieldWrapper = textField?.parentElement?.parentElement;
		const btnIcon = document.getElementById(`${props.id}-btn`)?.querySelector('.v-icon');

		textFieldWrapper?.setAttribute('id', `${props.id}-wrapper`);
		resultListId.value = textFieldWrapper?.getAttribute('aria-owns');
		textFieldWrapper?.removeAttribute('aria-owns');
		textFieldWrapper?.removeAttribute('aria-haspopup');
		textFieldWrapper?.removeAttribute('aria-expanded');
		textFieldWrapper?.removeAttribute('role');

		if (textFieldWrapper !== null && textFieldWrapper !== undefined) {
			observer.observe(textFieldWrapper, config);
		}

		if (btnIcon !== null && btnIcon !== undefined) {
			btnObserver.observe(btnIcon, config);
		}
	};

	const emitChangeEvent = (): void => {
		emit('change', inputValue.value);
		valid.value = inputAutoCompleteField.value.valid;
	};

	const emitFocusEvent = (): void => {
		focused.value = true;
		emit('focus', inputValue.value);
	};

	const emitBlurEvent = (): void => {
		focused.value = false;
		emit('click', inputValue.value);
		valid.value = inputAutoCompleteField.value.valid;
	};

	const emitEscEvent = (): void => {
		const emittedValue = inputAutoCompleteField.value.isMenuActive ? inputValue.value : '';
		emit('esc', emittedValue);
		valid.value = inputAutoCompleteField.value.valid;
	};

	const inputBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (focused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});
</script>

<style lang="scss" scoped>
	.amelipro-text-field {
		:deep(.v-input__slot) {
			min-height: $input-min-height !important;
			border: v-bind(inputBorderStyle);
			border-radius: $input-radius;

			fieldset {
				border: 0;
			}
		}
	}

	.amelipro-text-field__label {
		margin-bottom: 4px;
		font-weight: $label-font-weight;
	}
</style>
